import { useQuery } from '@tanstack/react-query';
import { useBunja } from 'bunja/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useMemo } from 'react';

import { getTradingCompetitionsListApi } from '@flipster/apis/v1/tradingCompetition';
import { abTestBunja } from '@flipster/features/src/shared/ab-test';
import { useAuth } from '@flipster/features/src/shared/auth/state';
import { OrderAttributionMapper, orderAttributionAtom } from '@flipster/features/src/shared/order-attribution';
import { useCheckSpotOnly } from '@flipster/features/src/shared/regulation';
import { tradingLeaderboardMyRankingBunja } from '@flipster/features/src/trading-leaderboard-page/bunja';
import { userPoolEligibilityBunja } from '@flipster/features/src/userpool/user-pool.state';
import { useCookie } from '@flipster/react/misc/browser/cookie';
import { useMounted } from '@flipster/react/misc/lifecycle/mount';
import { CookieKeys } from '@flipster/utils/cookie';
import segment from '@flipster/utils/segment';
import { GNBEvent } from '@flipster/utils/segment/events/gnb';

import { navBunja } from './bunja';

export function useNav() {
  const { getNavigation } = useBunja(navBunja);
  const [perpetualReferralSymbol] = useCookie(CookieKeys.ReferrerSymbol);
  const [spotReferrerSymbol] = useCookie(CookieKeys.SpotReferrerSymbol);

  const isMounted = useMounted();
  const { isLoggedIn, isCookieLoggedIn } = useAuth();
  const { myOptInIntervalRankingsAtom } = useBunja(tradingLeaderboardMyRankingBunja);
  const { userPoolEligibilityAtom } = useBunja(userPoolEligibilityBunja);
  const myTradingLeaderboardsRanking = useAtomValue(myOptInIntervalRankingsAtom);
  const userPoolEligibility = useAtomValue(userPoolEligibilityAtom);
  const { getVariantAtom } = useBunja(abTestBunja);
  const { variantAtom } = getVariantAtom('25q3-ti-usd1-points-dashboard');
  const variant = useAtomValue(variantAtom);
  const isEnabledUsd1PointsDashboard = isMounted && variant === 'treatment';
  const setOrderAttribution = useSetAtom(orderAttributionAtom);
  const isSpotOnly = useCheckSpotOnly();

  const enabledFeatureFlags = useMemo(() => {
    const flags = [];
    if (isEnabledUsd1PointsDashboard) flags.push('25q3-ti-usd1-points-dashboard');
    return flags;
  }, [isEnabledUsd1PointsDashboard]);

  const { data: competitions } = useQuery({
    queryKey: ['getTradingCompetitionsListApi', 'GNB', isCookieLoggedIn],
    queryFn: () => getTradingCompetitionsListApi(),
    select: (res) =>
      res.data.competitions
        .filter(
          (c) => (c.competitionStatus === 'UPCOMING' || c.competitionStatus === 'ONGOING') && !!c.participationTime,
        )
        .sort((a, b) => new Date(a.competitionStartTime).getTime() - new Date(b.competitionStartTime).getTime()),
    enabled: isCookieLoggedIn,
  });

  const navigations = useMemo(
    () =>
      getNavigation({
        isLoggedIn: isMounted && isLoggedIn,
        isSpotOnly,
        referrerSymbols: {
          perpetual: perpetualReferralSymbol,
          spot: spotReferrerSymbol,
        },
        userPoolEligibility,
        competitions,
        myTradingLeaderboardsRanking,
        enabledFeatureFlags,
      }),
    [
      isMounted,
      isLoggedIn,
      perpetualReferralSymbol,
      spotReferrerSymbol,
      competitions,
      myTradingLeaderboardsRanking,
      isSpotOnly,
      getNavigation,
      userPoolEligibility,
      enabledFeatureFlags,
    ],
  );

  const onClickLink = (name: string, category?: string) => {
    if (category) segment.track(GNBEvent.GnbMenuClick, { name, category });
    else segment.track(GNBEvent.GnbClick, { name });
    if (name === 'Trade' && category === 'Perpetual')
      setOrderAttribution(OrderAttributionMapper.trade.perpetualDefault);
    if (name === 'Trade' && category === 'Spot') setOrderAttribution(OrderAttributionMapper.trade.spotDefault);
  };

  return {
    navigations,
    onClickLink,
  };
}
